import React, { useMemo } from 'react';
import { useGPS } from '../../contexts/GPSContext';
import StatTile from '../ui/StatTileNew';
import StatCard from '../ui/StatCard';
import SortableTable from '../SortableTable';
import { safeParseDate } from '../../utils/dateUtils';
import { isDraftIssue, isResolvedIssue } from '../../utils/gpsStatusCategories';
import GPSSenseNotice from './GPSSenseNotice';

/**
 * GPS "Statuts" tab — resolution-time analytics.
 *
 * Philosophy: only genuinely RESOLVED tickets of the period (Definition of Done:
 * "Terminé(e)"/"Terminé" for interventions, "Approuvé" for requests — see
 * isResolvedIssue; the "Afficher" filter is pinned to "Fermés" and inactive).
 * Rejected/cancelled tickets are excluded (they skew the durations), and drafts
 * are excluded entirely (ticket AND "Brouillon" workflow step), like the Général
 * tab. It shows the average end-to-end resolution time and decomposes it across
 * the workflow steps the tickets went through.
 *
 * Data source: each issue carries `status_changes` — either the legacy per-status
 * summary ({ "<status>": { total_elapsed_time, … } }, seconds) or the transitions
 * journal ([{ from, to, at }], ordered) that replaces it upstream. Both reduce to
 * the seconds spent per status (elapsedSecondsByStatus); the sum over all steps
 * equals (status_date − created), so the steps decompose the total.
 */

const getComparisonLabel = (periodType) => {
  switch (periodType) {
    case 'week': return 'vs la semaine précédente';
    case 'month': return 'vs le mois précédent';
    case 'quarter': return 'vs le trimestre précédent';
    case 'year': return "vs l'année précédente";
    default: return 'vs la période précédente';
  }
};

// Vivid categorical palette, ordered so adjacent steps stay easy to tell apart
// (hue alternates across families). Applied longest → shortest.
const STEP_COLORS = ['#5b3df0', '#16a866', '#f5a623', '#ef4444', '#2f6df0', '#ec4899', '#14b8a6', '#f97316', '#0ea5e9', '#a855f7', '#64748b'];

// Steps where a ticket spends, on average, less than this are transient/erroneous
// (e.g. a status toggled by mistake) and are dropped from the breakdown.
const NOISE_THRESHOLD_MS = 5 * 60 * 1000;

// Seconds spent per status, from either `status_changes` format: the legacy
// per-status summary (total_elapsed_time, seconds) or the transitions journal
// ([{from, to, at}] — time in S = sum of (at − previous at) over the transitions
// leaving S, the previous of the first being `created`). Completed stays only,
// like the legacy summary. Accepts the VARIANT value or its JSON string; never
// throws; null when nothing is usable. Exported for its equivalence tests.
export function elapsedSecondsByStatus(issue) {
  let sc = issue?.status_changes;
  if (typeof sc === 'string') {
    try { sc = JSON.parse(sc); } catch (e) { return null; }
  }
  if (!sc || typeof sc !== 'object') return null;

  const seconds = {};
  if (Array.isArray(sc)) {
    let prev = safeParseDate(issue?.created);
    sc.forEach((t) => {
      const at = safeParseDate(t?.at);
      if (!at || Number.isNaN(at.getTime())) return;
      if (prev && !Number.isNaN(prev.getTime()) && t?.from) {
        const s = Math.floor((at - prev) / 1000);
        if (s > 0) seconds[t.from] = (seconds[t.from] || 0) + s;
      }
      prev = at;
    });
  } else {
    Object.entries(sc).forEach(([status, info]) => {
      const s = Number(info?.total_elapsed_time) || 0;
      if (s > 0) seconds[status] = s;
    });
  }
  return Object.keys(seconds).length ? seconds : null;
}

// Is a date within [start, end] (inclusive)? Used to scope to tickets actually
// CLOSED during the period (status_date = closure date), not merely created then.
function inPeriod(raw, start, end) {
  const d = safeParseDate(raw);
  return !!d && !Number.isNaN(d.getTime()) && d >= start && d <= end;
}

// End-to-end resolution time of a closed ticket (ms), or null when undatable.
function resolutionMs(issue) {
  const start = safeParseDate(issue.created);
  const end = safeParseDate(issue.status_date);
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.max(0, end - start);
}

function median(sortedMsArray) {
  const n = sortedMsArray.length;
  if (!n) return 0;
  const mid = Math.floor(n / 2);
  return n % 2 ? sortedMsArray[mid] : (sortedMsArray[mid - 1] + sortedMsArray[mid]) / 2;
}

function formatDuration(ms) {
  if (!ms || ms < 0) return '0';
  const min = ms / 60000;
  if (min < 60) return `${Math.max(1, Math.round(min))} min`;
  const hours = ms / 36e5;
  if (hours < 72) return `${Math.round(hours)} h`;
  const days = hours / 24;
  if (days <= 10) return `${Math.round(days)} j`;
  const weeks = Math.floor(days / 7);
  const remainingDays = Math.round(days % 7);
  return remainingDays === 0 ? `${weeks} sem` : `${weeks} sem ${remainingDays} j`;
}

const formatSignedPct = (cur, prev) => {
  if (!prev) return cur ? '+∞' : '--';
  const v = ((cur - prev) / prev) * 100;
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
};

// Average + median end-to-end resolution time over a list of closed issues.
function resolutionSummary(closed) {
  const durations = (closed || []).map(resolutionMs).filter((d) => d !== null);
  if (!durations.length) return { avgMs: 0, medianMs: 0, count: 0 };
  const total = durations.reduce((a, b) => a + b, 0);
  const sorted = durations.slice().sort((a, b) => a - b);
  return { avgMs: total / durations.length, medianMs: median(sorted), count: durations.length };
}

// Client-side validation steps: any status whose name contains "validation"
// (case-insensitive). Time spent there is on the client, not on us, so it is
// excluded from the "hors validation" resolution metric.
const isValidationStatus = (status) => (status || '').toLowerCase().includes('validation');

// Time (ms) a closed ticket spent in validation/draft steps, from status_changes.
// 0 when the ticket has no such steps (or no parseable history).
function excludedValidationMs(issue) {
  const seconds = elapsedSecondsByStatus(issue);
  if (!seconds) return 0;
  let ms = 0;
  Object.entries(seconds).forEach(([status, s]) => {
    if (isDraftIssue({ status }) || isValidationStatus(status)) ms += s * 1000;
  });
  return ms;
}

// Average resolution time EXCLUDING client-validation (and draft) time. Computed
// from the SAME wall-clock base as the total — per ticket, (status_date − created)
// minus the validation/draft time spent — over the SAME set of dated tickets, so
// the result is mathematically ALWAYS ≤ the total resolution time (a ticket with
// no validation step simply equals its total). Clamped at 0 in case status_changes
// over-reports (a ticket can revisit a status, inflating its elapsed time).
function resolutionExcludingValidationSummary(closed) {
  const durations = [];
  (closed || []).forEach((issue) => {
    const total = resolutionMs(issue);
    if (total === null) return;
    durations.push(Math.max(0, total - excludedValidationMs(issue)));
  });
  if (!durations.length) return { avgMs: 0, medianMs: 0, count: 0 };
  const sum = durations.reduce((a, b) => a + b, 0);
  const sorted = durations.slice().sort((a, b) => a - b);
  return { avgMs: sum / durations.length, medianMs: median(sorted), count: durations.length };
}

// Average element-shipping time: (reception_date − delivery_date) over the issues
// that carry both dates. Restored from main.
function shippingSummary(list) {
  const dated = (list || []).filter((i) => {
    const d = safeParseDate(i.delivery_date);
    const r = safeParseDate(i.reception_date);
    return d && r && !Number.isNaN(d.getTime()) && !Number.isNaN(r.getTime());
  });
  if (!dated.length) return { avgMs: 0, count: 0 };
  const total = dated.reduce((acc, i) => acc + Math.max(0, safeParseDate(i.reception_date) - safeParseDate(i.delivery_date)), 0);
  return { avgMs: total / dated.length, count: dated.length };
}

// Per-step aggregation from status_changes across the closed tickets.
function computeSteps(closed) {
  const agg = new Map(); // status -> { durationsMs[], totalMs }

  (closed || []).forEach((issue) => {
    const seconds = elapsedSecondsByStatus(issue);
    if (!seconds) return;
    Object.entries(seconds).forEach(([status, s]) => {
      if (isDraftIssue({ status })) return; // drafts excluded entirely (like Général)
      const ms = s * 1000;
      if (!agg.has(status)) agg.set(status, { durationsMs: [], totalMs: 0 });
      const e = agg.get(status);
      e.durationsMs.push(ms);
      e.totalMs += ms;
    });
  });

  const closedCount = (closed || []).length || 1;

  // Drop transient/noise steps (avg time per ticket below the threshold).
  const kept = Array.from(agg.entries())
    .map(([status, e]) => {
      const sorted = e.durationsMs.slice().sort((a, b) => a - b);
      return {
        status,
        tickets: e.durationsMs.length,
        avgMs: e.durationsMs.length ? e.totalMs / e.durationsMs.length : 0, // mean over tickets that went through
        medianMs: median(sorted),
        avgOfTotalMs: e.totalMs / closedCount, // contribution to the average ticket's total time
        totalMs: e.totalMs,
      };
    })
    .filter((s) => s.avgMs >= NOISE_THRESHOLD_MS);

  // Shares are recomputed over the kept steps so the breakdown stays at 100 %.
  const totalKeptMs = kept.reduce((a, s) => a + s.totalMs, 0);
  kept.forEach((s) => { s.share = totalKeptMs > 0 ? s.totalMs / totalKeptMs : 0; });

  // Longest first (drives the day-cells layout and the legend order).
  kept.sort((a, b) => b.avgOfTotalMs - a.avgOfTotalMs);
  return kept.map((s, i) => ({ ...s, color: STEP_COLORS[i % STEP_COLORS.length] }));
}

// ---- Time-decomposition: full-width proportional bar, longest first --------
function DecompositionBar({ steps }) {
  if (!steps.length) return null;
  const totalAvgMs = steps.reduce((a, s) => a + s.avgOfTotalMs, 0);

  return (
    <div className="bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-900 p-4 sm:p-6">
      <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Décomposition du temps moyen de résolution</h3>
        <span className="text-xs text-gray-400 dark:text-gray-500">100 % = {formatDuration(totalAvgMs)} en moyenne</span>
      </div>

      <div className="flex w-full h-8 rounded-lg overflow-hidden ring-1 ring-black/5 dark:ring-white/10">
        {steps.map((s) => (
          <div
            key={s.status}
            className="h-full flex items-center justify-center"
            style={{ flexBasis: `${Math.max(s.share * 100, 0)}%`, backgroundColor: s.color, minWidth: s.share > 0 ? 3 : 0 }}
            title={`${s.status} · ${formatDuration(s.avgOfTotalMs)} en moyenne (${Math.round(s.share * 100)} %)`}
          >
            {s.share >= 0.07 && (
              <span className="text-[11px] font-semibold text-white px-1 truncate drop-shadow-sm">{Math.round(s.share * 100)}%</span>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 mt-5">
        {steps.map((s) => (
          <div key={s.status} className="flex items-center gap-2 min-w-0">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1" title={s.status}>{s.status}</span>
            <span className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums whitespace-nowrap">{formatDuration(s.avgOfTotalMs)}</span>
            <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums w-9 text-right">{Math.round(s.share * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const GPSSLAs = () => {
  const gps = useGPS();
  const comparisonLabel = getComparisonLabel(gps.periodType);

  // Tickets actually RESOLVED during the period (Terminé(e)/Terminé/Approuvé, not
  // rejected/cancelled), drafts excluded, and closure date (status_date) within
  // the period — so a ticket merely created in the period (closed later) is not
  // counted. The tab pins "Afficher" to Fermés.
  const closed = useMemo(
    () => (gps.issues || []).filter((i) => isResolvedIssue(i) && !isDraftIssue(i) && inPeriod(i.status_date, gps.startDate, gps.endDate)),
    [gps.issues, gps.startDate, gps.endDate]
  );
  const prevClosed = useMemo(
    () => (gps.previousIssues || []).filter((i) => isResolvedIssue(i) && !isDraftIssue(i) && inPeriod(i.status_date, gps.previousStartDate, gps.previousEndDate)),
    [gps.previousIssues, gps.previousStartDate, gps.previousEndDate]
  );

  const resolution = useMemo(() => resolutionSummary(closed), [closed]);
  const prevResolution = useMemo(() => resolutionSummary(prevClosed), [prevClosed]);
  const resolutionExVal = useMemo(() => resolutionExcludingValidationSummary(closed), [closed]);
  const prevResolutionExVal = useMemo(() => resolutionExcludingValidationSummary(prevClosed), [prevClosed]);
  const shipping = useMemo(() => shippingSummary(closed), [closed]);
  const prevShipping = useMemo(() => shippingSummary(prevClosed), [prevClosed]);
  const steps = useMemo(() => computeSteps(closed), [closed]);

  const columns = useMemo(() => ([
    {
      key: 'status',
      label: 'Étape',
      render: (row) => (
        <span className="inline-flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: row.color }} />
          <span className="truncate">{row.status}</span>
        </span>
      ),
    },
    { key: 'tickets', label: 'Nb tickets' },
    { key: 'avgMs', label: 'Temps moyen', rawValue: (r) => r.avgMs, render: (r) => formatDuration(r.avgMs) },
    { key: 'medianMs', label: 'Temps médian', rawValue: (r) => r.medianMs, render: (r) => formatDuration(r.medianMs) },
    {
      key: 'share',
      label: 'Part du temps total',
      rawValue: (r) => r.share,
      render: (r) => (
        <span className="inline-flex items-center gap-2 w-full">
          <span className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden min-w-[40px]">
            <span className="block h-full rounded-full" style={{ width: `${Math.round(r.share * 100)}%`, backgroundColor: r.color }} />
          </span>
          <span className="tabular-nums text-xs w-10 text-right">{Math.round(r.share * 100)}%</span>
        </span>
      ),
    },
  ]), []);

  return (
    <div className="space-y-6">
      <GPSSenseNotice
        sense="resolved"
        message="Vue centrée sur les tickets résolus pendant la période, pour analyser le temps passé à chaque étape du workflow. Les tickets rejetés/annulés et les brouillons sont exclus."
      />

      <StatCard>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        <StatTile
          label={`Temps moyen de résolution (${resolution.count} ticket${resolution.count > 1 ? 's' : ''})`}
          value={resolution.count === 0 ? 'N/A' : formatDuration(resolution.avgMs)}
          variation={resolution.count === 0 ? '--' : formatSignedPct(resolution.avgMs, prevResolution.avgMs)}
          difference={resolution.count === 0 ? '--' : `${resolution.avgMs >= prevResolution.avgMs ? '+' : '-'}${formatDuration(Math.abs(resolution.avgMs - prevResolution.avgMs))}`}
          icon={(
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          color="blue"
          description="Temps moyen entre la création d'un ticket et sa résolution, sur les tickets résolus de la période (Terminé(e), Terminé ou Approuvé). Rejetés/annulés exclus."
          comparisonLabel={comparisonLabel}
          invertVariationColor
        />
        <StatTile
          label={`Temps moyen de résolution hors validation`}
          value={resolutionExVal.count === 0 ? 'N/A' : formatDuration(resolutionExVal.avgMs)}
          variation={resolutionExVal.count === 0 ? '--' : formatSignedPct(resolutionExVal.avgMs, prevResolutionExVal.avgMs)}
          difference={resolutionExVal.count === 0 ? '--' : `${resolutionExVal.avgMs >= prevResolutionExVal.avgMs ? '+' : '-'}${formatDuration(Math.abs(resolutionExVal.avgMs - prevResolutionExVal.avgMs))}`}
          icon={(
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          color="indigo"
          description="Temps moyen de résolution (création → clôture) duquel on soustrait, par ticket, le temps passé dans les statuts de validation (côté client, non imputable à nous) et en Brouillon. Toujours inférieur ou égal au temps de résolution total."
          comparisonLabel={comparisonLabel}
          invertVariationColor
        />
        <StatTile
          label={`Temps moyen d'envoi élément (${shipping.count} ticket${shipping.count > 1 ? 's' : ''})`}
          value={shipping.count === 0 ? 'N/A' : formatDuration(shipping.avgMs)}
          variation={shipping.count === 0 ? '--' : formatSignedPct(shipping.avgMs, prevShipping.avgMs)}
          difference={shipping.count === 0 ? '--' : `${shipping.avgMs >= prevShipping.avgMs ? '+' : '-'}${formatDuration(Math.abs(shipping.avgMs - prevShipping.avgMs))}`}
          icon={(
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          )}
          color="green"
          description="Temps moyen entre la date d'expédition et la date de réception d'un élément, sur les tickets terminés de la période disposant des deux dates."
          comparisonLabel={comparisonLabel}
          invertVariationColor
        />
        </div>
      </StatCard>

      <DecompositionBar steps={steps} />

      <div className="bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-900 shadow p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Temps passé par étape du workflow</h3>
        <SortableTable
          data={steps}
          columns={columns}
          emptyMessage="Aucun ticket fermé avec un historique de statuts sur cette période."
          exportable
          exportFilename="gps_temps_par_etape.csv"
          fullHeight
          disableFilters
        />
      </div>
    </div>
  );
};

export default GPSSLAs;
